import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import Cookies from "js-cookie";
import APICall from "./APICall"; // Ensure this path is correct
import { paths } from "./Constants"; // Ensure this path is correct

const AuthContext = createContext(undefined);
const JWT_COOKIE_NAME = "jwtToken";
const USER_ENTITY_ID_COOKIE_NAME = "user_entity_id"; // Define your new cookie name

const AuthProvider = ({ children }) => {
  const [jwt, setJwt] = useState(Cookies.get(JWT_COOKIE_NAME) || "");
  const [userEntityIdFromCookie, setUserEntityIdFromCookie] = useState(
    // New state for entity_id from cookie
    Cookies.get(USER_ENTITY_ID_COOKIE_NAME) || ""
  );
  const [user, setUser] = useState(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);

  const clearSessionData = useCallback(
    (logMessage = "Clearing session data.") => {
      console.log(`AuthProvider: ${logMessage}`);
      setJwt("");
      setUser(null);
      setUserEntityIdFromCookie(""); // Clear entity_id state
      Cookies.remove(JWT_COOKIE_NAME, { path: "/" });
      Cookies.remove(USER_ENTITY_ID_COOKIE_NAME, { path: "/" }); // Clear entity_id cookie
    },
    []
  );

  const fetchUserDetailsOnLoad = useCallback(
    async (currentTokenWithBearer, entityId) => {
      if (!currentTokenWithBearer || !entityId) {
        clearSessionData(
          "fetchUserDetailsOnLoad - Missing token or entityId from cookie."
        );
        setIsLoadingSession(false);
        return;
      }
      console.log(
        `AuthProvider: Attempting to fetch user details for entityId (from cookie on load): ${entityId}`
      );
      setIsLoadingSession(true);

      try {
        const result = await APICall(
          "GET",
          paths.user.fetch,
          undefined,
          { user_id: entityId }, // Data for GET (becomes query params)

          currentTokenWithBearer
        );

        console.log(
          "AuthProvider: User details fetch response (on load):",
          JSON.stringify(result, null, 2)
        );

        console.log(
          "fetchUserDetailsOnLoad - API Result: ",
          JSON.stringify(result, null, 2)
        );
        console.log(
          "fetchUserDetailsOnLoad - Checking condition: result.status === 'success'",
          result?.status === "success"
        );
        console.log(
          "fetchUserDetailsOnLoad - Checking condition: result.response exists",
          !!result?.response
        );
        if (result?.response) {
          console.log(
            "fetchUserDetailsOnLoad - Checking condition: result.response is user (has entity_id)",
            typeof result.response === "object" && result.response.entity_id
          );
          console.log(
            "fetchUserDetailsOnLoad - Checking condition: result.response.user is user (has entity_id)",
            result.response.user &&
              typeof result.response.user === "object" &&
              result.response.user.entity_id
          );
        }
        // THEN THE IF CONDITION:
        if (
          result &&
          result.status === "success" &&
          result.response &&
          ((typeof result.response === "object" && result.response.entity_id) ||
            (result.response.user &&
              typeof result.response.user === "object" &&
              result.response.user.entity_id))
        ) {
          // ... success path ...
        } else {
          console.error(
            "fetchUserDetailsOnLoad - Condition for successful user data extraction FAILED."
          ); // Add this log
          clearSessionData(/* ... */);
        }

        // Adjust this condition based on your actual API response structure for success
        if (
          result &&
          result.status === "success" &&
          result.response &&
          ((typeof result.response === "object" && result.response.entity_id) || // If response IS the user object
            (result.response.user &&
              typeof result.response.user === "object" &&
              result.response.user.entity_id)) // If response.user IS the user object
        ) {
          const userFromServer = result.response.user || result.response;
          setUser(userFromServer);
          console.log(
            "AuthProvider: User details fetched and set successfully on reload:",
            userFromServer
          );
        } else {
          clearSessionData(
            `Failed to fetch user details on load or invalid response. Result: ${JSON.stringify(result)}`
          );
        }
      } catch (error) {
        console.error(
          "AuthProvider: CATCH block. Error fetching user details on reload:",
          error
        );
        clearSessionData(
          `Error fetching user details on load: ${error.message}`
        );
      } finally {
        setIsLoadingSession(false);
      }
    },
    [clearSessionData]
  );

  // Effect for initial app load: check JWT and entityId cookie, then fetch user
  useEffect(() => {
    const initialJwt = Cookies.get(JWT_COOKIE_NAME);
    const initialEntityId = Cookies.get(USER_ENTITY_ID_COOKIE_NAME);

    console.log("AuthProvider useEffect: Initial JWT:", initialJwt);
    console.log(
      "AuthProvider useEffect: Initial Entity ID from cookie:",
      initialEntityId
    );

    if (initialJwt && initialEntityId) {
      setJwt(initialJwt);
      setUserEntityIdFromCookie(initialEntityId); // Set state for completeness, though direct value is used
      console.log(
        "AuthProvider useEffect: JWT and Entity ID found. Calling fetchUserDetailsOnLoad."
      );
      fetchUserDetailsOnLoad(initialJwt, initialEntityId);
    } else {
      if (!initialJwt) console.log("AuthProvider useEffect: No JWT found.");
      if (!initialEntityId)
        console.log("AuthProvider useEffect: No Entity ID cookie found.");
      // No need to clear session here if cookies are simply missing, means user is not logged in.
      // If one exists without the other, that's an inconsistent state, but fetchUserDetailsOnLoad would fail anyway.
      setIsLoadingSession(false); // No session to load if essential cookies are missing
    }
  }, [fetchUserDetailsOnLoad]); // Only fetchUserDetailsOnLoad as dep if clearSessionData is not used inside this effect directly

  const loginUser = (tokenParam, userFromLogin) => {
    const cookieOptions = {
      path: "/",
      // expires: 7, // Example: 7 days
      // secure: process.env.NODE_ENV === 'production', // Only HTTPS in production
      // sameSite: 'Lax',
    };
    console.log("AuthProvider: loginUser called (after OTP validation).");

    if (tokenParam && userFromLogin && userFromLogin.entity_id) {
      setJwt(tokenParam);
      setUser(userFromLogin);
      setUserEntityIdFromCookie(userFromLogin.entity_id); // Set entity_id state

      Cookies.set(JWT_COOKIE_NAME, tokenParam, cookieOptions);
      Cookies.set(
        USER_ENTITY_ID_COOKIE_NAME,
        userFromLogin.entity_id,
        cookieOptions
      ); // Set entity_id cookie

      setIsLoadingSession(false);
      console.log(
        "AuthProvider: JWT, User Data, and Entity ID cookie set. User:",
        userFromLogin
      );
    } else {
      console.error(
        "AuthProvider: loginUser called with invalid token, user data, or missing entity_id in userFromLogin."
      );
      clearSessionData("loginUser called with invalid parameters."); // Clears all session cookies and state
      setIsLoadingSession(false);
    }
  };

  const updateUserInContext = (updatedUserData) => {
    setUser((currentUser) => {
      const newUser = { ...currentUser, ...updatedUserData };
      console.log("AuthProvider: User data updated in context:", newUser);
      // If entity_id could change (unlikely for a primary ID), update the cookie too
      // if (currentUser && updatedUserData.entity_id && currentUser.entity_id !== updatedUserData.entity_id) {
      //   Cookies.set(USER_ENTITY_ID_COOKIE_NAME, updatedUserData.entity_id, { path: "/" });
      //   setUserEntityIdFromCookie(updatedUserData.entity_id);
      // }
      return newUser;
    });
  };

  const deleteSession = () => {
    clearSessionData("Deleting session (logout).");
    setIsLoadingSession(false);
  };

  const contextValue = {
    jwt,
    user,
    // userEntityId: userEntityIdFromCookie, // Optionally expose if needed directly by components
    isLoadingSession,
    loginUser,
    deleteSession,
    updateUserInContext,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider.");
  }
  return context;
};

export { AuthProvider, useAuth };
